<script lang="ts">
import { WritableComputedRef } from "vue";
import Discussion from "~~/models/Discussion";
import MessagesActionDialog from "~~/models/MessagesActionDialog";
import UserModel from "~~/models/UserModel";
import { PlatformEnum } from "~~/enums/Platform";
import Discussions from "~~/models/Discussions";

enum MessageDialogWindow {
  AllDiscussions = 0,
  Discussion = 1,
}

export default {
  name: "MessagesDialog",
  data() {
    return {
      currentWindow: MessageDialogWindow.AllDiscussions,
      discussions: null as unknown as WritableComputedRef<Discussions | null>,
      discussion: null as Discussion | null,
      discussionId: undefined as string | undefined,
      newMessage: "",
      newAttachments: new Array<{ id: string; filename: string } | null>(),
      userInformation: null as UserModel | null,
      discussionIsLoading: false,
      attachmentIsLoading: false,
      activateTransition: true,
      helper: useHelper(),
      MessageDialogWindow,
    };
  },
  props: {
    dialog: {
      type: MessagesActionDialog,
      default: null,
    },
  },
  watch: {
    dialog: {
      async handler(newValue, oldValue) {
        if (newValue) {
          if (newValue.displayDialog) {
            this.clearMessage();
            if (this.dialog.discussionId) {
              this.currentWindow = MessageDialogWindow.Discussion;
              this.discussionId = this.dialog.discussionId;
              await this.loadDiscussion();
            } else {
              this.currentWindow = MessageDialogWindow.AllDiscussions;
              this.discussionId = undefined;
              this.clearDiscussions();
              await this.loadDiscussions();
              if (
                !this.dialog.loadUnseenDiscussions &&
                this.discussions?.discussions?.length === 1
              ) {
                this.activateTransition = false;
                this.currentWindow = MessageDialogWindow.Discussion;
                this.discussionId = this.discussions.discussions[0].id;
                await this.loadDiscussion();
                this.activateTransition = true;
              }
            }
            this.setAttachments();
          } else {
            this.discussion = null;
            this.clearDiscussions();
          }
        }
      },
      deep: true,
    },
    newAttachments: {
      async handler(newValue, oldValue) {
        if (newValue) {
          this.setAttachments();
        }
      },
      deep: true,
    },
  },
  methods: {
    async loadDiscussions() {
      if (this.dialog.loadUnseenDiscussions) {
        //NOT IMPLEMENTED IN MyCIM
      } else {
        this.discussions = await useProspect().getDiscussions(
          this.dialog.prospectId
        );
      }
    },
    async loadDiscussion() {
      this.discussion = await useProspect().getDiscussion(
        this.discussionId,
        this.dialog.prospectId
      );
      nextTick(() => {
        this.scrollToBottom();
        setTimeout(() => {
          this.updateMessageStatus();
        }, 2000);
      });
    },
    goToAllDiscussions() {
      this.currentWindow = MessageDialogWindow.AllDiscussions;
      this.loadDiscussions();
    },
    goToDiscussion(discussionId: string) {
      this.discussionId = discussionId;
      this.currentWindow = MessageDialogWindow.Discussion;
      this.loadDiscussion();
    },
    async submitMessage() {
      var messageSubmitted = await useProspect().addMessage(
        this.discussion.prospectId,
        this.newMessage,
        this.newAttachments.filter((a) => !!a).map((a) => a!.id)
      );
      if (messageSubmitted) {
        await this.loadDiscussion();
        this.clearMessage();
      }
    },
    async updateMessageStatus() {
      //Get all messages created from PRM and not already seen by the prospect
      var messageIds = this.discussion?.messages
        ?.filter((m) => !m.seenOn && m.platform !== PlatformEnum.MyCim)
        .map((m) => m.id);

      if (messageIds && messageIds.length > 0) {
        //Update messages status in DB
        var messageSubmitted = await useProspect().updateMessageStatus(
          messageIds
        );

        //Update messages status in the UI
        if (messageSubmitted.isSuccess) {
          messageSubmitted.messages.forEach((message) => {
            this.discussion?.messages?.forEach((m) => {
              if (m.id === message.id) {
                m.seenBy = message.seenBy;
                m.seenOn = message.seenOn;
                if (
                  !m.seenByName &&
                  m.seenBy &&
                  m.seenBy === this.userInformation?.userId
                )
                  m.seenByName = this.userInformation?.emailAddress;
              }
            });
          });
        }
      }
    },
    setAttachments() {
      if (this.newAttachments?.length == 0) {
        this.newAttachments.push(null);
      } else if (this.newAttachments[this.newAttachments.length - 1] != null) {
        this.newAttachments.push(null);
      }
      if (
        this.newAttachments &&
        this.newAttachments.filter((att) => att == null).length > 1
      ) {
        this.newAttachments = this.newAttachments.filter((att) => att != null);
        this.newAttachments.push(null);
      }
    },
    clearMessage() {
      this.newMessage = "";
      this.newAttachments = [];
      this.newAttachments.push(null);
    },
    clearDiscussions() {
      this.discussions = null;
    },
    scrollToBottom() {
      document.querySelector("#message-anchor-bottom")!.scrollIntoView();
    },
  },
  computed: {
    messageIsEmpty() {
      return !this.newMessage?.trim();
    },
  },
  async created() {
    this.userInformation = computed(() => useUser().getUserInformation());

    this.discussionIsLoading = computed(() =>
      useProspect().getIsLoadingDiscussion()
    );

    this.attachmentIsLoading = computed(() =>
      useProspect().getIsLoadingAttachment()
    );
  },
};
</script>

<template>
  <template v-if="dialog != null">
    <v-dialog class="dialog-messages" v-model="dialog.displayDialog" persistent>
      <CoreLoader
        :visible="discussionIsLoading || attachmentIsLoading"
      ></CoreLoader>
      <v-card class="v-card-messages">
        <v-card-title class="v-card-messages-title">
          <v-row dense no-gutters>
            <v-col class="flex-grow-0 buttons">
              <span
                v-if="
                  dialog.loadUnseenDiscussions ||
                  (discussions?.discussions &&
                    discussions.discussions.length > 1)
                "
                class="v-card-messages-back"
              >
                <v-icon
                  v-if="currentWindow === MessageDialogWindow.Discussion"
                  @click="goToAllDiscussions()"
                  >mdi-arrow-left
                </v-icon>
              </span>
            </v-col>
            <v-col class="flex-grow-1">
              {{ dialog.title }}
            </v-col>
            <v-col class="flex-grow-0 buttons">
              <v-icon
                class="v-card-messages-close"
                @click="dialog.displayDialog = false"
                >mdi-close
              </v-icon>
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider></v-divider>
        <v-window
          v-model="currentWindow"
          class="v-window-wrapper"
          :touch="false"
        >
          <v-window-item
            :key="MessageDialogWindow.AllDiscussions"
            :transition="activateTransition"
          >
            <div class="discussions">
              <template v-for="disc in discussions?.discussions">
                <div
                  class="discussion-wrapper"
                  @click="goToDiscussion(disc.id)"
                >
                  <div class="discussion-header">
                    <div class="discussion-title">
                      <span>
                        {{ disc.prospectName }}
                      </span>
                    </div>
                    <div class="discussion-manager">
                      <span v-if="disc.messages && disc.messages.length > 0">
                        {{ disc.managerName }}
                      </span>
                    </div>
                    <div class="discussion-date">
                      <span v-if="disc.messages && disc.messages.length > 0">
                        <span
                          v-if="dialog?.displaySeenChecks"
                          class="sender-seen"
                        >
                          <v-icon
                            class="picto"
                            :class="{ visible: disc.messages[0].seenOn }"
                            color="#25D366"
                            >mdi-check-all
                          </v-icon>
                        </span>
                        {{
                          helper.convertDateToString(
                            new Date(disc.messages[0].createdOn),
                            true
                          )
                        }}
                      </span>
                    </div>
                  </div>
                  <div class="discussion-content">
                    <span
                      v-if="disc.messages && disc.messages.length > 0"
                      v-html="
                        helper.secureStringAndReplaceNewLineToBr(
                          disc.messages[0].content
                        )
                      "
                    ></span>
                  </div>
                </div>
              </template>
            </div>
          </v-window-item>
          <v-window-item
            :key="MessageDialogWindow.Discussion"
            :transition="activateTransition"
          >
            <v-card-text class="v-card-messages-text">
              <template v-if="discussion">
                <div class="messages-wrapper">
                  <v-card
                    rounded
                    elevation="1"
                    v-for="(message, index) in discussion.messages"
                    class="message"
                    :class="{
                      mine: userInformation?.userId === message.createdBy,
                    }"
                  >
                    <div class="sender">
                      <span v-if="message.createdByName">{{
                        $t("messages.messageBy", {
                          username: message.createdByName,
                        })
                      }}</span>
                      <span
                        >&nbsp;{{
                          $t("messages.messageOn", {
                            date: helper.convertDateToString(
                              new Date(message.createdOn),
                              true
                            ),
                          })
                        }}
                      </span>
                      <span
                        v-if="dialog?.displaySeenChecks"
                        class="sender-seen"
                      >
                        <v-tooltip
                          v-if="message.seenOn"
                          activator="parent"
                          location="top"
                          offset="-10"
                          >{{
                            $t("messages.messageSeen", {
                              username: message.seenByName,
                              date: helper.convertDateToString(
                                new Date(message.seenOn),
                                true
                              ),
                            })
                          }}
                        </v-tooltip>
                        <v-icon
                          v-if="dialog?.displaySeenChecks"
                          class="picto"
                          :class="{ visible: message.seenOn }"
                          color="#25D366"
                          >mdi-check-all
                        </v-icon>
                      </span>
                    </div>
                    <div
                      class="content mt-1"
                      v-html="
                        helper.secureStringAndReplaceNewLineToBr(
                          message.content
                        )
                      "
                    ></div>
                    <div v-if="message.attachments" class="message-attachments">
                      <div
                        v-for="attachment in message.attachments"
                        class="message-attachment"
                      >
                        <v-icon color="rgba(0, 0, 0, 0.87)"
                          >mdi-paperclip</v-icon
                        >
                        <a
                          class="ml-2"
                          href="#"
                          :title="
                            attachment.filename ?? $t('attachment.fileNoName')
                          "
                          @click.prevent.self="
                            useProspect().downloadAttachment(
                              attachment.attachmentId,
                              attachment.filename ?? $t('attachment.fileNoName')
                            )
                          "
                        >
                          {{
                            attachment.filename ?? $t("attachment.fileNoName")
                          }}
                        </a>
                      </div>
                    </div>
                  </v-card>
                </div>
                <span id="message-anchor-bottom"></span>
              </template>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-textarea
                class="new-message"
                :placeholder="$t('messages.addMessage')"
                clearable
                persistent-clear
                :hide-details="true"
                rows="5"
                variant="solo"
                bg-color="white"
                v-model="newMessage"
                autocomplete="off"
                aria-autocomplete="none"
              ></v-textarea>
            </v-card-actions>
            <v-row no-gutters class="v-card-messages-buttons">
              <v-col>
                <template v-for="(attachment, i) in newAttachments">
                  <CoreAttachment
                    class="new-message-attachments"
                    :input-label="$t('messages.addAttachment')"
                    display-label=""
                    :can-delete="true"
                    :hide-details="true"
                    density="compact"
                    v-model="newAttachments[i]"
                  ></CoreAttachment>
                </template>
              </v-col>
              <v-col class="submission">
                <v-btn
                  class="btn-cim"
                  :disabled="messageIsEmpty"
                  @click="submitMessage()"
                >
                  <v-icon>mdi-check</v-icon>
                  {{ $t("messages.submit") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-window-item>
        </v-window>
      </v-card>
    </v-dialog>
  </template>
</template>

<style scoped lang="scss">
.dialog-messages {
  max-width: 550px;
}

.v-card-messages {
  background-color: #f6f7fc;
  height: 550px;
}

.v-card-messages-title {
  font-weight: bold;
  overflow: visible;

  .buttons {
    height: 32px;
  }
}

.v-card-messages-back {
  display: inline-block;
  width: 30px;
  height: 30px;
}

.v-card-messages-close {
  cursor: pointer;
}

.v-dialog {
  .v-overlay__content {
    .v-card-messages-text {
      flex-grow: 1;
      overflow: auto;
      padding: 8px;
    }
  }
}

.v-window-wrapper {
  flex-grow: 1;

  :deep(.v-window__container) {
    height: 100%;
  }

  .v-window-item {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}

.discussions {
  height: 100%;
  overflow: auto;
}

.discussion-wrapper {
  cursor: pointer;
  padding: 10px;
  border-bottom: solid 1px #ddd;

  &:nth-child(odd) {
    background-color: #fff;
  }
}

.discussion-header {
  display: flex;
}

.discussion-title {
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: bold;
}

.discussion-manager {
  flex-grow: 0;
  white-space: nowrap;
  font-size: 13px;
  line-height: 20px;
  font-style: italic;
  margin-left: 10px;
}

.discussion-date {
  flex-grow: 0;
  align-self: flex-end;
  white-space: nowrap;
  font-size: 12px;
  line-height: 20px;
  color: #777;
  font-style: italic;
  margin-left: 10px;
}

.sender-seen {
  display: inline-block;
  width: 18px;
  height: 18px;
  position: relative;
  top: -2px;

  .picto {
    visibility: hidden;

    &.visible {
      visibility: visible;
    }
  }
}

.discussion-content {
  line-height: 20px;
  height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
}

.messages-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  justify-content: flex-end;

  .message {
    background-color: #fee2ef;
    margin-top: 8px;
    padding: 10px;
    border-radius: 5px;
    width: 70%;

    &.mine {
      background-color: #fff;
      align-self: flex-end;
    }
  }

  .sender {
    font-size: 12px;
    color: #777;
    font-style: italic;
    text-align: right;
  }

  .sender-seen {
    display: inline-block;
    width: 18px;
    height: 18px;
    margin-left: 4px;
    position: relative;
    top: -2px;

    .picto {
      visibility: hidden;
      opacity: 0;

      &.visible {
        visibility: visible;
        opacity: 1;
        transition: opacity 0.5s linear;
      }
    }
  }

  .content {
    font-size: 14px;
  }

  .message-attachment {
    margin-top: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    a {
      &:link,
      &:visited,
      &:active {
        color: #E05C91;
        text-decoration: none;
      }

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.new-message {
  :deep(textarea)::placeholder {
    opacity: 1;
    color: #888;
  }
}

.new-message-attachments {
  margin-bottom: 5px;
}

.v-card-messages-buttons {
  flex-grow: 0;
  padding: 0 8px 8px 8px;

  .submission {
    flex-grow: 0;
    padding-left: 10px;
  }
}
</style>
